import type { WorkflowInvoiceLine, WorkflowInvoiceSourceLineCandidate } from '../../types/workflowInvoice';
import { asQuantityUnit, buildQuantitySetFromPair, quantitySetToFields } from '../../utils/commercialQuantity';
import { extractWarehouseQuantitySetPatch, type WarehouseQuantityPatch } from '../warehouse/warehouseQuantityConversion';

export type SourceQuantitySet = NonNullable<WorkflowInvoiceSourceLineCandidate['sourceQuantitySet']>;

/**
 * Persisted physical set of a source order/ZD line. A line saved before the
 * quantity-set era carries only `quantity` + `unit`; that single unit is its
 * whole snapshot. Returns null when nothing physical is persisted.
 */
export function sourceQuantitySetFromLine(line: {
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
}): SourceQuantitySet | null {
  const set: SourceQuantitySet = {
    pcs: line.quantityPcs ?? null,
    m2: line.quantityM2 ?? null,
    m3: line.quantityM3 ?? null,
  };
  if (set.pcs != null || set.m2 != null || set.m3 != null) return set;
  const unit = asQuantityUnit(line.unit);
  if (unit && line.quantity != null && Number(line.quantity) > 0) {
    return { ...set, [unit]: Number(line.quantity) };
  }
  return null;
}

/**
 * Drops every derived unit the source line never persisted. The server rejects
 * such units (`INVOICE_QUANTITY_UNRESOLVED`) instead of backfilling them from
 * catalog geometry. Without a known source set the derived set is kept.
 */
export function restrictQuantitySetToSource<T extends Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>>(
  fields: T,
  sourceSet: SourceQuantitySet | null | undefined,
): T {
  if (!sourceSet) return fields;
  return {
    ...fields,
    quantityPcs: sourceSet.pcs == null ? null : fields.quantityPcs,
    quantityM2: sourceSet.m2 == null ? null : fields.quantityM2,
    quantityM3: sourceSet.m3 == null ? null : fields.quantityM3,
  };
}

export function hasInvoicePhysicalQuantitySet(line: Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>): boolean {
  return [line.quantityPcs, line.quantityM2, line.quantityM3].some(value => value != null);
}

/** New modal values carry a pair; this adapter owns its first persisted set. */
export function invoiceQuantityPatch(
  line: WorkflowInvoiceLine,
  patch: WarehouseQuantityPatch,
  sourceSet?: SourceQuantitySet | null,
): Partial<WorkflowInvoiceLine> {
  const existingSet = extractWarehouseQuantitySetPatch(patch);
  const acceptedPair = {
    physicalQuantity: patch.quantity,
    physicalUnit: asQuantityUnit(patch.unit),
    pricingQuantity: patch.pricingQuantity ?? patch.quantity,
    pricingUnit: asQuantityUnit(patch.pricingUnit ?? patch.unit),
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? undefined,
  };
  const firstSet = hasInvoicePhysicalQuantitySet(existingSet)
    ? null
    : buildQuantitySetFromPair(acceptedPair, line);
  return {
    quantity: patch.quantity,
    unit: acceptedPair.physicalUnit,
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? null,
    ...restrictQuantitySetToSource(firstSet ? quantitySetToFields(firstSet) : existingSet, sourceSet),
    commercialQuantity: patch.pricingQuantity ?? null,
    commercialUnit: patch.pricingUnit ?? null,
  };
}

// ---------------------------------------------------------------------------
// WP11 (docs/decisions/2026-09-24-wp11-faktury-czesciowe.md): partial
// invoices. A source line carries its uninvoiced remainder from the order's
// `invoiceCoverage`; a newly linked invoice line without a quantity is
// prefilled with that remainder (the operator may lower it for a partial
// invoice or raise it — over-invoicing asks for the explicit order alignment
// at approval).

const PHYSICAL_UNITS = ['pcs', 'm2', 'm3'] as const;
type PhysicalUnit = typeof PHYSICAL_UNITS[number];
const isPhysicalUnit = (unit: unknown): unit is PhysicalUnit => (PHYSICAL_UNITS as readonly unknown[]).includes(unit);
const roundQuantity = (value: number) => Math.round(value * 1000) / 1000;

/** Coverage of a source order line (subset of the coverage read model). */
export interface SourceLineCoverage {
  unit: string | null;
  remainingQuantity: number | null;
}

/**
 * Adds the uninvoiced remainder (display quantity + scaled physical set) to a
 * candidate. Unknown coverage or quantities leave the candidate unchanged.
 */
export function withCoverageRemainder(
  candidate: WorkflowInvoiceSourceLineCandidate,
  coverage: SourceLineCoverage | null | undefined,
): WorkflowInvoiceSourceLineCandidate {
  const set = candidate.sourceQuantitySet;
  if (!coverage || !set || !isPhysicalUnit(coverage.unit) || coverage.remainingQuantity == null) return candidate;
  const ordered = set[coverage.unit];
  if (ordered == null || ordered <= 0) return candidate;
  const ratio = coverage.remainingQuantity / ordered;
  const scale = (value: number | null) => (value == null ? null : roundQuantity(value * ratio));
  return {
    ...candidate,
    remainingQuantity: coverage.remainingQuantity,
    remainingUnit: coverage.unit,
    remainingQuantitySet: { pcs: scale(set.pcs), m2: scale(set.m2), m3: scale(set.m3) },
  };
}

/**
 * True when the line's quantity (in the remainder's unit) exceeds the source
 * line's uninvoiced remainder — over-invoicing, shown as a hint only
 * (approval asks for the explicit order alignment). Unknown → false.
 */
export function exceedsRemainder(
  line: Pick<WorkflowInvoiceLine, 'quantity' | 'unit' | 'quantityPcs' | 'quantityM2' | 'quantityM3'>,
  candidate: WorkflowInvoiceSourceLineCandidate | null | undefined,
): boolean {
  const unit = candidate?.remainingUnit;
  const remaining = candidate?.remainingQuantity;
  if (!candidate?.remainingQuantitySet || !isPhysicalUnit(unit) || remaining == null) return false;
  const field = ({ pcs: 'quantityPcs', m2: 'quantityM2', m3: 'quantityM3' } as const)[unit];
  const quantity = line[field] ?? (line.unit === unit ? line.quantity : null);
  return quantity != null && quantity > remaining + 0.0005;
}

/**
 * Quantity fields for a line just linked to `candidate`: its uninvoiced
 * remainder, physical set and commercial pair (the source's price unit when
 * that unit is in the set). null when the line already has a quantity or the
 * remainder is unknown or zero — nothing is overwritten.
 */
export function remainderPrefill(
  line: Pick<WorkflowInvoiceLine, 'quantity' | 'quantityPcs' | 'quantityM2' | 'quantityM3'>,
  candidate: WorkflowInvoiceSourceLineCandidate,
): Partial<WorkflowInvoiceLine> | null {
  if (line.quantity != null || hasInvoicePhysicalQuantitySet(line)) return null;
  const set = candidate.remainingQuantitySet;
  const unit = candidate.remainingUnit;
  const quantity = candidate.remainingQuantity;
  if (!set || !isPhysicalUnit(unit) || quantity == null || quantity <= 0 || set[unit] == null) return null;
  const pricingUnit: PhysicalUnit = isPhysicalUnit(candidate.sourcePricingUnit) && set[candidate.sourcePricingUnit] != null
    ? candidate.sourcePricingUnit
    : unit;
  return {
    quantity,
    unit,
    quantityPcs: set.pcs,
    quantityM2: set.m2,
    quantityM3: set.m3,
    conversionUnit: null,
    conversionQuantity: null,
    commercialQuantity: pricingUnit === unit ? quantity : set[pricingUnit],
    commercialUnit: pricingUnit,
  };
}
